import { finalizeCardmarketBackupBatch, saveCardmarketBackup } from 'lib/api'
import { useErrorHandler } from 'lib/hooks'
import { useFetchCardmarketStock } from 'lib/hooks/useFetchCardmarketStock'
import { useGradingScale } from 'lib/hooks/useGradingScale'
import { FetchedStockInterface } from 'lib/hooks/useStockStore'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  GAME_IDS,
  GAME_NAMES,
  IdGame,
  InventoryArticle,
  InventoryFetchState,
  isSingle,
  ONBOARDING_SOURCE_SYNC,
  ONBOARDING_STEPS,
} from 'shared'
import { CompleteDataInGradedCards } from '../inventory/CompleteDataInGradedCards'
import { checkIfThereAreGradedCards } from './onboarding-utils'
import { checkIfFetchedStockCommentContainsTaxationKeywords } from './OnboardingPresenter'

// Max articles per backup request: keeps each chunk small enough to stay well
// under request-size limits, so very large stocks are sent in several parts.
const CM_BACKUP_CHUNK_SIZE = 5000

export interface FetchInventoryProps {
  setVisitedSteps: (value: React.SetStateAction<number[]>) => void
  setOnboardingStep: (newOnboardingStep: number) => void
  inventoryState: FetchedStockInterface
  setInventoryState: React.Dispatch<React.SetStateAction<FetchedStockInterface | undefined>>
  downloadStockSource: string
}

export const FetchInventory = ({
  setVisitedSteps,
  setOnboardingStep,
  inventoryState,
  setInventoryState,
  downloadStockSource,
}: FetchInventoryProps): JSX.Element => {
  const { t } = useTranslation()
  const { handle } = useErrorHandler()
  // const { enqueueSnackbar } = useSnackbar()
  const fetchCardmarketStock = useFetchCardmarketStock()
  const { loading: gradingScaleLoading } = useGradingScale()
  // const fetchCtStock = useFetchCardTraderStock()
  // const [fetchedFromCt, setFetchedFromCt] = useState(false)
  const [thereAreGradedCards, setThereAreGradedCards] = useState(false)
  const [fetchState, setFetchState] = useState(InventoryFetchState.FETCHING)
  const [fetchingInventory, setFetchingInventory] = useState(false)
  const [workingFetchedStock, setWorkingFetchedStock] = useState<InventoryArticle[]>([])

  const [currentlyFetchingGame, setCurrentlyFetchingGame] = useState<string>('')
  const [currentlyFetchingStockType, setCurrentlyFetchingStockType] = useState<string>('')

  // Records how many backup parts were sent per idGame during the fetch. Passed
  // to finalize so the server knows which stored chunks to reassemble into the
  // zip, without needing to list the storage bucket.
  const backupManifestRef = useRef<Record<number, number>>({})

  // fetchedFromCt
  fetchState

  /**
   * Fetch stock from Cardmarket, It's the wrapper call
   */
  const fetchCmStock = useCallback(
    async (tokens): Promise<FetchedStockInterface> => {
      setFetchingInventory(true)
      let newState = {
        stock: [] as InventoryArticle[],
        sealedStock: [] as InventoryArticle[],
        fetchedAt: '',
      }
      setFetchState(InventoryFetchState.FETCHING)
      try {
        const { stock, sealedStock } = await fetchUserStockAllGames(tokens)

        newState = {
          stock,
          sealedStock,
          fetchedAt: new Date().toISOString(),
        }
        setFetchState(InventoryFetchState.IDLE)
      } catch (error: any) {
        handle(error)
        if (error?.response?.data?.error?.message === 'missingCardmarketConnection') {
          setFetchState(InventoryFetchState.FAILED_DISCONNECTED_ACCOUNT)
          throw new Error('missingCardmarketConnection')
        } else {
          setFetchState(InventoryFetchState.FAILED_UNKNOWN)
        }
      }
      setCurrentlyFetchingGame('completed')
      setFetchingInventory(false)
      return newState
    },
    [fetchCardmarketStock]
  )

  /**
   * Best-effort backup of the raw fetched stock. The stock is split into parts
   * to keep each request small (large sellers), each part is retried once, and a
   * final failure never blocks onboarding: the backup is a secondary concern.
   * Returns the number of parts attempted, so the caller can build the finalize
   * manifest. A part that fails both attempts is still counted: the zip step
   * tolerates a missing chunk rather than losing the whole game.
   */
  const backupFetchedStock = async (
    idGame: IdGame,
    articles: InventoryArticle[]
  ): Promise<number> => {
    if (articles.length === 0) return 0

    const parts = Math.ceil(articles.length / CM_BACKUP_CHUNK_SIZE)
    for (let part = 0; part < parts; part++) {
      const chunk = articles.slice(part * CM_BACKUP_CHUNK_SIZE, (part + 1) * CM_BACKUP_CHUNK_SIZE)
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          await saveCardmarketBackup(idGame, part, chunk)
          break
        } catch (e) {
          if (attempt === 1) console.error(`CM backup failed: game ${idGame}, part ${part}`, e)
        }
      }
    }
    return parts
  }

  /**
   *
   * Fetch all games from Cardmarket
   */
  const fetchUserStockAllGames = async (tokens: { key: string; secret: string } | null) => {
    const stock: InventoryArticle[] = []
    const sealedStock: InventoryArticle[] = []
    const manifest: Record<number, number> = {}

    for (const idGame of GAME_IDS) {
      setCurrentlyFetchingGame(GAME_NAMES[idGame])
      setCurrentlyFetchingStockType('stock')
      const gameStockData = await fetchCardmarketStock(idGame as IdGame, tokens)
      const parts = await backupFetchedStock(
        idGame as IdGame,
        gameStockData.stock.concat(gameStockData.sealedStock)
      )
      if (parts > 0) manifest[idGame] = parts
      stock.push(...gameStockData.stock)
      sealedStock.push(...gameStockData.sealedStock)
    }

    setCurrentlyFetchingGame('')
    setCurrentlyFetchingStockType('generic accessories')
    const genericStockData = await fetchCardmarketStock(99 as IdGame, tokens)
    const genericParts = await backupFetchedStock(99 as IdGame, genericStockData.sealedStock)
    if (genericParts > 0) manifest[99] = genericParts
    sealedStock.push(...genericStockData.sealedStock)

    backupManifestRef.current = manifest

    setCurrentlyFetchingStockType('')
    return { stock, sealedStock }
  }

  // const fetchCardTrader = async () => {
  //   try {
  //     setFetchingInventory(true)
  //     const response = await fetchCtStock()

  //     setFetchedFromCt(true)
  //     setWorkingFetchedStock(response)

  //     setFetchingInventory(false)
  //   } catch (e) {
  //     console.error('Errore fetchCtStock:', e) // stack + message in DevTools

  //     if (e instanceof Error) {
  //       console.error('message:', e.message)
  //       console.error('stack  :', e.stack)
  //       // mostra anche eventuali cause nidificate (Node 18 / Chrome > 94)
  //       // console.error('cause  :', e.cause);
  //       // oppure tutte le proprietà, anche quelle non enumerable:
  //       console.log(
  //         'props  :',
  //         Object.getOwnPropertyNames(e).reduce(
  //           (acc, key) => ({ ...acc, [key]: (e as any)[key] }),
  //           {}
  //         )
  //       )
  //     } else {
  //       // se non è un Error “classico”, serializza come fallback
  //       console.error('unknown error object:', JSON.stringify(e))
  //     }

  //     enqueueSnackbar(t('onboarding.handleCtConnectionError'), { variant: 'error' })
  //   }
  // }

  const startCardmarketFetch = async () => {
    const inventory = await fetchCmStock(null)

    // Fire-and-forget: trigger the server-side zip without blocking onboarding.
    finalizeCardmarketBackupBatch(backupManifestRef.current).catch((e) =>
      console.error('CM backup finalize failed', e)
    )

    inventory.stock.forEach((article) => {
      article.locations = [
        {
          _id: '',
          userId: '',
          ptArticleId: 0,
          name: article.comment ?? '',
          quantity: article.quantity,
          deltaQuantity: article.quantity,
          totalPickedQuantity: 0,
          totalQuantityWithIssues: 0,
        },
      ]
    })

    inventory.sealedStock.forEach((item) => {
      item.locations = [
        {
          _id: '',
          userId: '',
          ptArticleId: 0,
          name: item.comment ?? '',
          quantity: item.quantity,
          deltaQuantity: item.quantity,
          totalPickedQuantity: 0,
          totalQuantityWithIssues: 0,
        },
      ]
    })

    setWorkingFetchedStock(inventory.stock.concat(inventory.sealedStock))

    const thereAreGradedCards = checkIfThereAreGradedCards(inventory.stock)
    if (thereAreGradedCards) setThereAreGradedCards(thereAreGradedCards)
    else checkDifferentCommentsOrTaxationComments()
  }

  const checkDifferentCommentsOrTaxationComments = () => {
    const containsKeywords = checkIfFetchedStockCommentContainsTaxationKeywords(workingFetchedStock)

    const stockData: InventoryArticle[] = []
    const sealedStockData: InventoryArticle[] = []

    workingFetchedStock.forEach((article) => {
      if (isSingle(article.card)) stockData.push(article)
      else sealedStockData.push(article)
    })
    const newState: FetchedStockInterface = {
      stock: stockData,
      sealedStock: sealedStockData,
      fetchedAt: new Date().toISOString(),
    }
    setInventoryState(newState)

    if (containsKeywords) {
      setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.COMMENTS_FORM.idx])
      setOnboardingStep(ONBOARDING_STEPS.COMMENTS_FORM.idx)
    } else {
      setVisitedSteps((prev) => [...prev, ONBOARDING_STEPS.SET_PRICES.idx])
      setOnboardingStep(ONBOARDING_STEPS.SET_PRICES.idx)
    }
  }

  const handleConfirmGradedCards = (newIncomingStock: InventoryArticle[]) => {
    setWorkingFetchedStock(newIncomingStock)
    setThereAreGradedCards(false)
  }

  /**
   * avvia il fetch in base alla scelta utente
   */
  // Not on mount: the grades a company awards decide which of the seller's comments we can read, and the scale
  // arrives from the server a moment later. Starting before it is here would parse the first game against an empty
  // scale. A failed request also ends the wait — onboarding goes on with the shape check alone rather than hanging.
  const fetchStarted = useRef(false)

  useEffect(() => {
    if (gradingScaleLoading || fetchStarted.current) return

    fetchStarted.current = true

    if (downloadStockSource === ONBOARDING_SOURCE_SYNC.CARDMARKET) {
      startCardmarketFetch()
    }
    // if (downloadStockSource === ONBOARDING_SOURCE_SYNC.CARDTRADER) {
    //   fetchCardTrader()
    // }
  }, [gradingScaleLoading])

  /**
   * una volta finito il fetch, se non ci sono carte gradate (o sono già state sistemate) verifica
   * che non ci siano commenti particolari che identifichino la tassazione. se ci sono, sposta
   * il wizard nello step giusto
   */
  useEffect(() => {
    if (!fetchingInventory && workingFetchedStock.length !== 0 && !thereAreGradedCards) {
      checkDifferentCommentsOrTaxationComments()
    }
  }, [fetchingInventory, inventoryState, thereAreGradedCards])

  return (
    <div className="wizard-content container">
      {fetchingInventory && (
        <div className="text-center">
          <h1 className="fs-3">
            {t('onboarding.loading')} {downloadStockSource}
          </h1>
          {downloadStockSource === ONBOARDING_SOURCE_SYNC.CARDMARKET && (
            <div>
              <h4 className="fs-5 fw-normal">
                Fetching {currentlyFetchingGame} {currentlyFetchingStockType}...
              </h4>
            </div>
          )}
          <br />
          <i className="fa-solid fa-gear fa-spin spinner-gradient"></i>
          <p>{t('onboarding.doNotRefreshPage')}</p>
        </div>
      )}

      {!fetchingInventory && thereAreGradedCards && (
        <CompleteDataInGradedCards
          incomingStock={workingFetchedStock}
          handleConfirmGradedCards={handleConfirmGradedCards}
        />
      )}
    </div>
  )
}
